import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  Phone as PhoneIcon,
  Mail,
  Cake,
  Save,
  Check,
  Merge,
  Minus,
  Pencil,
  Gift,
  Plus,
  X,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import {
  cn,
  formatCurrency,
  formatDateHuman,
  formatDateTimeHuman,
  formatPKRCompact,
  formatRelative,
} from '@/lib/utils'
import { MAX_CUSTOMER_PHONE_LEN } from '@/lib/customerPhone'
import {
  DAY_OPTIONS,
  MONTH_OPTIONS,
  formatMonthDay,
  splitMonthDay,
  toMonthDayISO,
} from '@/lib/birthday'
import type {
  APIResponse,
  CustomerDetail,
  CustomerLoyaltyHistory,
  CustomerOrderHistoryItem,
  CustomerUpdateConflict,
  LifecycleStage,
  UpdateCustomerRequest,
} from '@/types'
import { STAGE_BADGE, STAGE_LABEL } from './AdminCustomersDirectory'
import { AdminCustomerAddresses } from './AdminCustomerAddresses'
import { AdminCustomerMergeDialog } from './AdminCustomerMergeDialog'
import { CustomerOrderDrawer } from './customers/CustomerOrderDrawer'
import { CustomerHouseAccountsPanel } from './customers/CustomerHouseAccountsPanel'
import { useHouseAccountsEnabled } from '@/hooks/useHouseAccountsEnabled'
import { useEdition } from '@/hooks/useEdition'

interface AdminCustomerProfileProps {
  customerID: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Profile dialog opened from a row in AdminCustomersDirectory.
 *
 * Layout: a one-line header (name + lifecycle badge + Find duplicates),
 * then two panes. Left: lifetime spend, visit facts, loyalty balance,
 * editable contact fields, WhatsApp consent and notes — fixed on desktop.
 * Right: tabs for order history, the loyalty ledger and the address book,
 * each scrolling on its own. Notes are capped at 4000 chars by the server.
 */
export function AdminCustomerProfile({
  customerID,
  open,
  onOpenChange,
}: AdminCustomerProfileProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // F-MM-N30: the house-account API is POS-only (RequirePOS).
  const houseAccounts = useHouseAccountsEnabled()
  const { posEnabled } = useEdition()
  // F-MM-N31: staff meal allowances moved to Manage Staff, so this tab is
  // for tabs — plus a pointer when an allowance was made on this customer
  // before the move. Same query key as the panel, so it's fetched once.
  const legacyStaffQuery = useQuery({
    queryKey: ['house-accounts', 'customer', customerID],
    queryFn: () => apiClient.listHouseAccounts({ customer_id: customerID! }),
    enabled: Boolean(open && customerID && posEnabled && houseAccounts.staff && !houseAccounts.tabsVisible),
  })
  const hasLegacyStaff = (legacyStaffQuery.data?.data ?? []).some(
    (a) => a.kind === 'staff_allowance' && a.status !== 'closed',
  )
  const houseAccountsShown = posEnabled && (houseAccounts.tabsVisible || (houseAccounts.staff && hasLegacyStaff))

  const detailQuery = useQuery<APIResponse<CustomerDetail>>({
    queryKey: ['customer', customerID],
    queryFn: () => apiClient.getCustomer(customerID as string),
    enabled: Boolean(customerID) && open,
    staleTime: 15_000,
  })

  const ordersQuery = useQuery<APIResponse<CustomerOrderHistoryItem[]>>({
    queryKey: ['customer', customerID, 'orders'],
    queryFn: () => apiClient.getCustomerOrderHistory(customerID as string, 50),
    enabled: Boolean(customerID) && open,
    staleTime: 30_000,
  })

  const loyaltyQuery = useQuery<APIResponse<CustomerLoyaltyHistory>>({
    queryKey: ['customer', customerID, 'loyalty-history'],
    queryFn: () => apiClient.getCustomerLoyaltyHistory(customerID as string),
    enabled: Boolean(customerID) && open,
    staleTime: 30_000,
  })

  const detail = detailQuery.data?.data
  const orders = ordersQuery.data?.data ?? []
  const loyalty = loyaltyQuery.data?.data
  const loyaltyEvents = loyalty?.events ?? []

  const [notes, setNotes] = useState<string>('')
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [tab, setTab] = useState<ProfileTab>('orders')
  const [receiptOrder, setReceiptOrder] = useState<CustomerOrderHistoryItem | null>(null)

  // Every customer opens on Orders, with no receipt open — a tab left on
  // Addresses, or a drawer left open, would be a surprise on the next visit.
  // The parent keeps this component mounted while closed, so reset on `open`
  // too rather than relying on unmount.
  useEffect(() => {
    setTab('orders')
    setReceiptOrder(null)
  }, [customerID, open])

  // Hydrate the textarea whenever a new customer's detail arrives.
  useEffect(() => {
    if (detail) setNotes(detail.notes ?? '')
  }, [detail])

  const notesMutation = useMutation({
    mutationFn: (value: string) =>
      apiClient.updateCustomerNotes(customerID as string, value),
    onSuccess: () => {
      setSavedAt(Date.now())
      queryClient.invalidateQueries({ queryKey: ['customer', customerID] })
      // The list page shows lifetime spend + visit count + stage — none
      // of which notes affect — so we don't refetch ['customers','list'].
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Notes not saved', description: msg, variant: 'destructive' })
    },
  })

  // Single mutation shared by every editable field. Each call sends one
  // key from UpdateCustomerRequest. Centralising it keeps cache
  // invalidation in one place and makes the conflict handling uniform.
  const fieldMutation = useMutation<
    APIResponse<CustomerDetail | CustomerUpdateConflict>,
    Error,
    { patch: UpdateCustomerRequest; label: string }
  >({
    mutationFn: ({ patch }) =>
      apiClient.updateCustomer(customerID as string, patch),
    onSuccess: (res, vars) => {
      // 4xx responses (including 409) come through as success === false
      // because the axios layer doesn't throw on 2xx-only — handle them
      // here as if they were errors.
      if (!res.success) {
        const code = res.error
        if (code === 'phone_conflict' || code === 'email_conflict') {
          const conflict = res.data as CustomerUpdateConflict | undefined
          const name = conflict?.conflicting_customer_name || 'another customer'
          const field = code === 'phone_conflict' ? 'phone' : 'email'
          toast({
            title: `Couldn't save ${field}`,
            description: `This ${field} is already on ${name}'s profile. Open it from the directory to merge.`,
            variant: 'destructive',
          })
          return
        }
        toast({
          title: `${vars.label} not saved`,
          description: res.message || 'Save failed',
          variant: 'destructive',
        })
        return
      }
      // Happy path: replace the cached detail with the fresh row the
      // server just returned so the modal refreshes instantly, and
      // invalidate the directory list since name/phone/email/birthday
      // are all visible there.
      const fresh = res.data as CustomerDetail
      queryClient.setQueryData<APIResponse<CustomerDetail>>(
        ['customer', customerID],
        (prev) => ({
          success: true,
          message: prev?.message ?? 'ok',
          data: fresh,
        }),
      )
      queryClient.invalidateQueries({ queryKey: ['customers', 'list'] })
      setSavedAt(Date.now())
    },
    onError: (err, vars) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: `${vars.label} not saved`, description: msg, variant: 'destructive' })
    },
  })

  const isDirty = detail ? (notes ?? '') !== (detail.notes ?? '') : false
  const saving = notesMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[95vh] max-w-6xl flex-col gap-0 overflow-hidden p-0 md:h-[88vh]">
        {!detail ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Header — one quiet line. The facts that used to live here
                (first / last visit) are in the left column now, next to
                the other numbers, so the header only says who this is. */}
            <DialogHeader className="shrink-0 space-y-0 border-b border-border/70 px-5 py-3 pr-12">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <DialogTitle asChild>
                  <div className="flex min-w-0 items-center gap-3 text-lg">
                    <EditableName
                      value={detail.display_name ?? ''}
                      onSave={(v) =>
                        fieldMutation.mutateAsync({
                          patch: { display_name: v },
                          label: 'Name',
                        })
                      }
                      saving={
                        fieldMutation.isPending &&
                        fieldMutation.variables?.patch.display_name !== undefined
                      }
                    />
                    {detail.lifecycle_stage && (
                      <Badge
                        variant="outline"
                        className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${STAGE_BADGE[detail.lifecycle_stage as LifecycleStage]}`}
                      >
                        {STAGE_LABEL[detail.lifecycle_stage as LifecycleStage]}
                      </Badge>
                    )}
                  </div>
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Customer profile, order history and loyalty activity.
                </DialogDescription>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setMergeOpen(true)}
                  className="h-8 shrink-0 gap-1.5 text-xs"
                >
                  <Merge className="h-3.5 w-3.5" />
                  Find duplicates
                </Button>
              </div>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
              {/* Left column — who they are and what they are worth. Fixed
                  on desktop so it never scrolls away while reading orders. */}
              <aside className="shrink-0 space-y-5 border-b border-border/70 px-5 py-4 md:w-[300px] md:overflow-y-auto md:border-b-0 md:border-r">
                <div>
                  <div className="text-3xl font-semibold leading-none tabular-nums tracking-tight">
                    {formatPKRCompact(detail.lifetime_spend)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {formatCurrency(detail.lifetime_spend)} spent in total
                  </div>
                  <dl className="mt-4 space-y-1.5 text-sm">
                    <FactRow label="Visits" value={detail.visit_count.toLocaleString()} />
                    <FactRow
                      label="Average bill"
                      value={detail.avg_ticket != null ? formatCurrency(detail.avg_ticket) : '—'}
                    />
                    <FactRow
                      label="Last visit"
                      value={detail.last_visit_at ? formatRelative(detail.last_visit_at) : 'Never'}
                    />
                    <FactRow
                      label="Customer since"
                      value={detail.first_visit_at ? formatDateHuman(detail.first_visit_at) : '—'}
                    />
                  </dl>
                </div>

                {loyalty?.enabled && (
                  <div className="rounded-md border border-border/70 px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Gift className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium tabular-nums">
                          {loyalty.balance}
                          <span className="font-normal text-muted-foreground">
                            {' '}/ {loyalty.threshold}
                          </span>
                        </span>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setAdjustOpen(true)}
                        className="h-7 gap-1 text-xs"
                      >
                        <Plus className="h-3 w-3" />
                        Adjust
                      </Button>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {loyalty.balance >= loyalty.threshold ? (
                        <span className="font-medium text-emerald-700 dark:text-emerald-400">
                          Reward ready — {loyalty.reward_label}
                        </span>
                      ) : (
                        <>visits toward {loyalty.reward_label}</>
                      )}
                    </div>
                  </div>
                )}

                <div className="divide-y divide-border/70">
                  <EditableContactCell
                    icon={PhoneIcon}
                    label="Phone"
                    value={detail.phone_normalized || detail.phone || ''}
                    rawValue={detail.phone ?? detail.phone_normalized ?? ''}
                    href={detail.phone_normalized ? `tel:${detail.phone_normalized}` : undefined}
                    inputType="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="0301 1234567"
                    hint="Pakistani numbers are stored as +92…"
                    onSave={(v) =>
                      fieldMutation.mutateAsync({
                        patch: { phone: v },
                        label: 'Phone',
                      })
                    }
                    saving={
                      fieldMutation.isPending &&
                      fieldMutation.variables?.patch.phone !== undefined
                    }
                  />
                  <EditableContactCell
                    icon={Mail}
                    label="Email"
                    value={detail.email ?? ''}
                    rawValue={detail.email ?? ''}
                    href={detail.email ? `mailto:${detail.email}` : undefined}
                    inputType="email"
                    autoComplete="email"
                    placeholder="name@example.com"
                    onSave={(v) =>
                      fieldMutation.mutateAsync({
                        patch: { email: v },
                        label: 'Email',
                      })
                    }
                    saving={
                      fieldMutation.isPending &&
                      fieldMutation.variables?.patch.email !== undefined
                    }
                  />
                  <EditableBirthdayCell
                    value={detail.birthday ?? ''}
                    onSave={(v) =>
                      fieldMutation.mutateAsync({
                        patch: { birthday: v },
                        label: 'Birthday',
                      })
                    }
                    saving={
                      fieldMutation.isPending &&
                      fieldMutation.variables?.patch.birthday !== undefined
                    }
                  />
                  {/* WhatsApp consent — opt-out model (2026-09-04): on by
                      default, this is the staff-side way out (the guest's own
                      way is replying STOP). Gates every business-initiated
                      WhatsApp send to this guest. */}
                  <label className="flex items-start gap-2 py-2.5 text-sm">
                    <Checkbox
                      checked={detail.whatsapp_opt_in ?? false}
                      disabled={
                        fieldMutation.isPending &&
                        fieldMutation.variables?.patch.whatsapp_opt_in !== undefined
                      }
                      onCheckedChange={(v) =>
                        fieldMutation.mutateAsync({
                          patch: { whatsapp_opt_in: v === true },
                          label: 'WhatsApp consent',
                        })
                      }
                      className="mt-0.5"
                      aria-label="WhatsApp offers and updates"
                    />
                    <span>
                      WhatsApp offers and updates
                      <span className="block text-xs text-muted-foreground">
                        Untick if the guest asked not to be messaged.
                      </span>
                    </span>
                  </label>
                </div>

                {/* Notes */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Notes</span>
                    <div className="flex items-center gap-2">
                      {savedAt && !isDirty && Date.now() - savedAt < 4000 && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600">
                          <Check className="h-3 w-3" /> Saved
                        </span>
                      )}
                      {isDirty && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={saving}
                          onClick={() => notesMutation.mutate(notes)}
                          className="h-7 gap-1.5 text-xs"
                        >
                          {saving ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Save className="h-3 w-3" />
                          )}
                          Save
                        </Button>
                      )}
                    </div>
                  </div>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Allergies, favourite table, regular order, anything the team should know."
                    rows={4}
                    maxLength={4000}
                    className="resize-none text-sm"
                  />
                </div>
              </aside>

              {/* Right pane — activity, one history per tab. */}
              <Tabs
                value={tab}
                onValueChange={(v) => setTab(v as ProfileTab)}
                className="flex min-h-0 min-w-0 flex-1 flex-col"
              >
                <TabsList className="h-auto w-full shrink-0 justify-start gap-4 rounded-none border-b border-border/70 bg-transparent px-5 py-0">
                  <ProfileTabTrigger value="orders">
                    Orders
                    <TabCount n={orders.length} loading={ordersQuery.isLoading} />
                  </ProfileTabTrigger>
                  {loyalty?.enabled && (
                    <ProfileTabTrigger value="loyalty">
                      Loyalty
                      <TabCount n={loyaltyEvents.length} loading={loyaltyQuery.isLoading} />
                    </ProfileTabTrigger>
                  )}
                  <ProfileTabTrigger value="addresses">Addresses</ProfileTabTrigger>
                  {houseAccountsShown && <ProfileTabTrigger value="accounts">Tab</ProfileTabTrigger>}
                </TabsList>

                <TabsContent value="orders" className="mt-0 min-h-0 flex-1 md:overflow-y-auto">
                  {ordersQuery.isLoading ? (
                    <TabSpinner />
                  ) : orders.length === 0 ? (
                    <TabEmpty>
                      No orders yet. Orders land here when this customer is attached at checkout.
                    </TabEmpty>
                  ) : (
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-background">
                        <TableRow>
                          <TableHead className="h-10 px-2.5 pl-5 text-xs font-normal">Order #</TableHead>
                          <TableHead className="h-10 px-2.5 text-xs font-normal">When</TableHead>
                          <TableHead className="h-10 px-2.5 text-xs font-normal">Type</TableHead>
                          <TableHead className="h-10 px-2.5 text-xs font-normal">Status</TableHead>
                          <TableHead className="h-10 px-2.5 text-right text-xs font-normal">Items</TableHead>
                          <TableHead className="h-10 px-2.5 text-right text-xs font-normal">Discount</TableHead>
                          <TableHead className="h-10 px-2.5 pr-5 text-right text-xs font-normal">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((o) => (
                          <TableRow
                            key={o.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => setReceiptOrder(o)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setReceiptOrder(o)
                              }
                            }}
                            className={cn(
                              'cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none',
                              o.cancelled_by_credit_note && 'opacity-60',
                            )}
                          >
                            <TableCell className="whitespace-nowrap px-2.5 pl-5 font-mono text-xs">
                              {o.order_number}
                            </TableCell>
                            <TableCell className="whitespace-nowrap px-2.5 text-xs">
                              {formatDateTimeHuman(o.completed_at || o.created_at)}
                            </TableCell>
                            <TableCell className="px-2.5 text-xs capitalize">
                              {o.order_type.replace(/_/g, ' ')}
                            </TableCell>
                            <TableCell className="px-2.5">
                              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                                {o.cancelled_by_credit_note ? 'Credited' : o.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-2.5 text-right text-xs tabular-nums">
                              {o.item_count}
                            </TableCell>
                            <TableCell className="px-2.5 text-right text-xs tabular-nums text-muted-foreground">
                              {o.discount_amount > 0 ? `−${formatCurrency(o.discount_amount)}` : '—'}
                            </TableCell>
                            <TableCell className="px-2.5 pr-5 text-right text-xs font-medium tabular-nums">
                              {formatCurrency(o.total_amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>

                {/* Loyalty — a merged earn/redeem timeline so support can
                    answer "did they already use their reward?". */}
                {loyalty?.enabled && (
                  <TabsContent value="loyalty" className="mt-0 min-h-0 flex-1 md:overflow-y-auto">
                    {loyaltyQuery.isLoading ? (
                      <TabSpinner />
                    ) : loyaltyEvents.length === 0 ? (
                      <TabEmpty>No loyalty activity yet.</TabEmpty>
                    ) : (
                      <ul className="divide-y divide-border/70">
                        {loyaltyEvents.map((ev, i) => (
                          <li
                            key={`${ev.type}-${ev.order_id}-${i}`}
                            className={`flex items-baseline gap-4 px-5 py-2.5 text-sm ${ev.voided ? 'opacity-50' : ''}`}
                          >
                            <span className="w-36 shrink-0 text-xs text-muted-foreground">
                              {formatDateTimeHuman(ev.at)}
                            </span>
                            <span className="min-w-0 flex-1">
                              {ev.type === 'earned' ? (
                                <span
                                  className={
                                    ev.voided
                                      ? 'text-muted-foreground line-through'
                                      : 'text-emerald-700 dark:text-emerald-400'
                                  }
                                >
                                  {/* A single earn event can be worth several
                                      stamps under the per-item earn basis, so
                                      show the count rather than letting a
                                      5-stamp order look like a 1-stamp one and
                                      leaving the balance unexplained. Rows
                                      written before per-item earning have no
                                      punch_count — treat those as 1. */}
                                  {ev.source === 'manual'
                                    ? `Manual: +${ev.punch_count ?? 1}${ev.note ? ` (${ev.note})` : ''}`
                                    : (ev.punch_count ?? 1) > 1
                                    ? `+${ev.punch_count} stamps earned`
                                    : 'Visit earned'}
                                  {ev.voided ? ' (reversed)' : ''}
                                  {/* Attribution — the cashier who took the
                                      payment or the manager behind a grant.
                                      Absent on guest-initiated punches. */}
                                  {ev.created_by_name && (
                                    <span className="text-muted-foreground">
                                      {' '}by {ev.created_by_name}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span>
                                  Reward redeemed
                                  <span className="text-muted-foreground">
                                    {' '}−{ev.punches_spent} visits,{' '}
                                    {formatCurrency(ev.discount_applied ?? 0)} off
                                  </span>
                                </span>
                              )}
                            </span>
                            <span className="shrink-0 font-mono text-xs text-muted-foreground">
                              {ev.order_number ?? ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </TabsContent>
                )}

                {/* Address book (F-FE-N30) — same rows the storefront account
                    and counter prefill use. */}
                <TabsContent value="addresses" className="mt-0 min-h-0 flex-1 px-5 py-4 md:overflow-y-auto">
                  <AdminCustomerAddresses customerID={detail.id} />
                </TabsContent>

                {houseAccountsShown && (
                  <TabsContent value="accounts" className="mt-0 min-h-0 flex-1 px-5 py-4 md:overflow-y-auto">
                    <CustomerHouseAccountsPanel customerID={detail.id} />
                  </TabsContent>
                )}
              </Tabs>
            </div>

            <CustomerOrderDrawer
              order={receiptOrder}
              onClose={() => setReceiptOrder(null)}
            />
            <AdminCustomerMergeDialog
              sourceID={customerID}
              sourceName={detail.display_name}
              open={mergeOpen}
              onOpenChange={setMergeOpen}
              onMerged={() => {
                // Close the profile after a merge — the row no longer
                // exists from the directory's POV.
                onOpenChange(false)
              }}
            />
            {loyalty?.enabled && customerID && (
              <AdjustPunchesDialog
                customerID={customerID}
                customerName={detail.display_name ?? ''}
                open={adjustOpen}
                onOpenChange={setAdjustOpen}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

type ProfileTab = 'orders' | 'loyalty' | 'addresses' | 'accounts'

function ProfileTabTrigger({ value, children }: { value: ProfileTab; children: React.ReactNode }) {
  return (
    <TabsTrigger
      value={value}
      className="-mb-px gap-1.5 rounded-none border-b-2 border-transparent px-0 py-3 text-sm shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
    >
      {children}
    </TabsTrigger>
  )
}

function TabCount({ n, loading }: { n: number; loading: boolean }) {
  if (loading) return null
  return <span className="text-xs tabular-nums text-muted-foreground">{n}</span>
}

function TabSpinner() {
  return (
    <div className="flex h-40 items-center justify-center">
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    </div>
  )
}

function TabEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-40 items-center justify-center px-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  )
}

function FactRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{value}</dd>
    </div>
  )
}

/** How many punches one manual grant can hand out. Mirrors the server cap
 *  (maxManualPunchGrant in marketing_loyalty_adjust.go) — change together. */
const MAX_MANUAL_PUNCHES = 5

/**
 * Manual punch grant ("give my friend +3"). PIN-gated like credit notes: the
 * grant is attributed to the admin/manager whose PIN authorises it, and the
 * required reason lands in the ledger — the audit trail is the control.
 * Positive adjustments only; refunds reverse punches through the existing
 * void paths.
 */
function AdjustPunchesDialog({
  customerID,
  customerName,
  open,
  onOpenChange,
}: {
  customerID: string
  customerName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [punches, setPunches] = useState(1)
  const [reason, setReason] = useState('')
  const [pin, setPin] = useState('')

  // Fresh form every open — a cancelled grant must not leak into the next one.
  useEffect(() => {
    if (open) {
      setPunches(1)
      setReason('')
      setPin('')
    }
  }, [open])

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.adjustCustomerLoyalty(customerID, {
        punches,
        reason: reason.trim(),
        pin: pin.trim(),
      }),
    onSuccess: (res) => {
      // 4xx comes through as success === false (axios layer doesn't throw).
      if (!res.success) {
        toast({
          title: 'Punches not added',
          description:
            res.error === 'invalid_pin'
              ? 'That PIN does not match any active admin or manager.'
              : res.message || 'Adjustment failed',
          variant: 'destructive',
        })
        return
      }
      toast({
        title: 'Punches added',
        description: `+${res.data?.granted} — balance is now ${res.data?.balance}/${res.data?.threshold}.`,
      })
      queryClient.invalidateQueries({ queryKey: ['customer', customerID, 'loyalty-history'] })
      onOpenChange(false)
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Punches not added', description: msg, variant: 'destructive' })
    },
  })

  const pinIsValid = /^\d{4}$/.test(pin.trim())
  const canSubmit = reason.trim().length > 0 && pinIsValid && !mutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Adjust punches</DialogTitle>
          <DialogDescription>
            Add visits to {customerName || 'this customer'}&apos;s loyalty card. The grant is
            recorded in the ledger with your name and reason.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Punches to add
            </label>
            <div className="flex items-center justify-center gap-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 w-9 px-0"
                onClick={() => setPunches((p) => Math.max(1, p - 1))}
                disabled={punches <= 1 || mutation.isPending}
                aria-label="Fewer punches"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-12 text-center text-2xl font-semibold tabular-nums">
                +{punches}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 w-9 px-0"
                onClick={() => setPunches((p) => Math.min(MAX_MANUAL_PUNCHES, p + 1))}
                disabled={punches >= MAX_MANUAL_PUNCHES || mutation.isPending}
                aria-label="More punches"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="adjust-reason"
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              Reason (required)
            </label>
            <Input
              id="adjust-reason"
              placeholder="e.g. Goodwill — missed stamp on last visit"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="adjust-pin"
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              Admin / Manager PIN (4 digits)
            </label>
            <Input
              id="adjust-pin"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="font-mono text-center tracking-[0.5em]"
              disabled={mutation.isPending}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => mutation.mutate()} disabled={!canSubmit}>
              {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Add {punches} {punches === 1 ? 'punch' : 'punches'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// EditableContactCell is the inline-edit version of the old read-only
// ContactCell. Display mode shows the value (and a pencil button on
// hover); edit mode swaps in an <Input> with Cancel / Save controls
// that mirror the Notes section styling.
//
// `value` is what to render in display mode (e.g. normalised phone);
// `rawValue` is what populates the input in edit mode (the raw user
// input — for phone, that's the un-normalised string the cashier
// originally typed).
/**
 * Birthday, edited as a month and a day (F-FE-N32).
 *
 * Its own component rather than a mode on EditableContactCell: two controls, a
 * validity rule spanning both, and a clear action are different enough that
 * folding them in would make the shared cell answer to two masters.
 *
 * Deliberately not `<input type="date">`, which this cell used to be. A date
 * picker structurally demands a year, and the join form has decided not to ask
 * for one — so most birthdays now carry a placeholder year that a date picker
 * would display as fact.
 *
 * The year is never shown here even for counter-captured birthdays that have a
 * real one. Showing it for those and not the others would advertise exactly the
 * distinction the design keeps out of the interface.
 */
function EditableBirthdayCell({
  value,
  onSave,
  saving,
}: {
  /** Stored ISO date, or '' for none. May carry a real year or the placeholder. */
  value: string
  /** Receives an ISO date to set, or '' to clear. */
  onSave: (value: string) => Promise<unknown>
  saving: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [month, setMonth] = useState<number | ''>('')
  const [day, setDay] = useState<number | ''>('')

  // Re-hydrate on every open so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (editing) {
      const s = splitMonthDay(value)
      setMonth(s.month)
      setDay(s.day)
    }
  }, [editing, value])

  const next = toMonthDayISO(month, day)
  const cleared = month === '' && day === ''
  // Half-filled is neither a valid birthday nor a clear, so there is nothing
  // honest to save — the Save button stays disabled and says why.
  const halfFilled = !cleared && next == null
  const dirty = cleared ? value !== '' : next != null && next !== value

  async function handleSave() {
    if (saving || halfFilled || !dirty) return
    await onSave(cleared ? '' : (next as string))
    setEditing(false)
  }

  const selectClass =
    'h-8 w-full rounded-md border border-input bg-background px-2 text-sm ' +
    'focus:outline-none focus:ring-1 focus:ring-ring'

  return (
    <div className="group py-2.5">
      <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Cake className="h-3 w-3" aria-hidden /> Birthday
        </span>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:outline-none text-muted-foreground hover:text-foreground"
            aria-label="Edit birthday"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5 space-y-1.5">
          <div className="grid grid-cols-[1.4fr_1fr] gap-1.5">
            <select
              className={selectClass}
              aria-label="Birthday month"
              value={month}
              onChange={(e) => setMonth(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Month</option>
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              className={selectClass}
              aria-label="Birthday day"
              value={day}
              onChange={(e) => setDay(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Day</option>
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <p className="text-[10px] text-muted-foreground">
            {halfFilled
              ? month !== '' && day !== ''
                ? 'That day does not exist in that month.'
                : 'Pick both a month and a day, or clear both.'
              : 'Day and month only — the year is not stored.'}
          </p>
          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              className="h-7 gap-1 text-xs"
              disabled={saving}
            >
              <X className="h-3 w-3" />
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSave}
              disabled={!dirty || halfFilled || saving}
              className="h-7 gap-1.5 text-xs"
            >
              {saving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-1 truncate text-sm font-medium text-foreground">
          {formatMonthDay(value) || (
            <span className="font-normal text-muted-foreground">—</span>
          )}
        </div>
      )}
    </div>
  )
}

function EditableContactCell({
  icon: Icon,
  label,
  value,
  rawValue,
  href,
  inputType = 'text',
  inputMode,
  autoComplete,
  placeholder,
  hint,
  onSave,
  saving,
}: {
  icon: typeof PhoneIcon
  label: string
  value: string
  rawValue: string
  href?: string
  inputType?: 'text' | 'email' | 'tel' | 'date'
  inputMode?: 'text' | 'tel' | 'email'
  autoComplete?: string
  placeholder?: string
  hint?: string
  onSave: (value: string) => Promise<unknown>
  saving: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(rawValue)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Re-hydrate the draft every time we enter edit mode so we never show
  // stale input from a previous open / close.
  useEffect(() => {
    if (editing) {
      setDraft(rawValue)
      // Focus on the next tick so the input has mounted.
      const t = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [editing, rawValue])

  const dirty = draft.trim() !== rawValue.trim()

  async function handleSave() {
    if (!dirty || saving) return
    await onSave(draft.trim())
    setEditing(false)
  }

  return (
    <div className="group py-2.5">
      <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Icon className="h-3 w-3" aria-hidden /> {label}
        </span>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:outline-none text-muted-foreground hover:text-foreground"
            aria-label={`Edit ${label.toLowerCase()}`}
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5 space-y-1.5">
          <Input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSave()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setEditing(false)
              }
            }}
            type={inputType}
            inputMode={inputMode}
            autoComplete={autoComplete}
            placeholder={placeholder}
            maxLength={
              inputType === 'email'
                ? 255
                : inputType === 'tel'
                ? MAX_CUSTOMER_PHONE_LEN
                : 100
            }
            className="h-8 text-sm"
            aria-label={label}
          />
          {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              className="h-7 gap-1 text-xs"
              disabled={saving}
            >
              <X className="h-3 w-3" />
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="h-7 gap-1.5 text-xs"
            >
              {saving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-1 text-sm font-medium text-foreground truncate">
          {value ? (
            href ? (
              <a href={href} className="hover:underline">
                {value}
              </a>
            ) : (
              value
            )
          ) : (
            <span className="text-muted-foreground font-normal">—</span>
          )}
        </div>
      )}
    </div>
  )
}

// EditableName is the header variant — large text, no border, pencil on
// hover. Same edit affordance as EditableContactCell so the interaction
// pattern is consistent across the dialog.
function EditableName({
  value,
  onSave,
  saving,
}: {
  value: string
  onSave: (v: string) => Promise<unknown>
  saving: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (editing) {
      setDraft(value)
      const t = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [editing, value])

  const dirty = draft.trim() !== value.trim()

  async function handleSave() {
    if (!dirty || saving) return
    await onSave(draft.trim())
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSave()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setEditing(false)
            }
          }}
          maxLength={100}
          placeholder="Customer name"
          className="h-8 max-w-xs text-base font-semibold"
          aria-label="Customer name"
        />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setEditing(false)}
          className="h-7 gap-1 text-xs"
          disabled={saving}
        >
          <X className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="h-7 gap-1.5 text-xs"
        >
          {saving ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Save className="h-3 w-3" />
          )}
          Save
        </Button>
      </div>
    )
  }

  return (
    <span
      className="group inline-flex items-center gap-2 max-w-full"
    >
      <span className="truncate">
        {value || (
          <span className="italic text-muted-foreground">unnamed customer</span>
        )}
      </span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:outline-none text-muted-foreground hover:text-foreground"
        aria-label="Edit name"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </span>
  )
}
